import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/axios'
import { API_ROUTES } from '@/constants/routes'
import { useAuthStore } from '@/stores/useAuthStore'
import type { Material } from '@/api/useMaterials'
import type { PrintSize } from '@/api/usePrintSizes'
import type { PriceConfig, VolumeTier } from '@/api/usePrices'
import type { ShippingConfig } from '@/api/useShippingConfig'

// ─── Materials ───────────────────────────────────────────────────────────────

const MATERIAL_KEYS = {
    all: ['admin-materials'] as const,
    list: () => [...MATERIAL_KEYS.all, 'list'] as const,
}

export function useAdminMaterials() {
    const { isLogged } = useAuthStore()
    return useQuery({
        queryKey: MATERIAL_KEYS.list(),
        queryFn: async () => {
            const { data } = await api.get<{ results: Material[] } | Material[]>(API_ROUTES.adminMaterials)
            return Array.isArray(data) ? data : data.results
        },
        enabled: isLogged,
    })
}

export function useCreateMaterial() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload: Omit<Material, 'id'>) => {
            const { data } = await api.post<Material>(API_ROUTES.adminMaterials, payload)
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: MATERIAL_KEYS.all })
        },
    })
}

export function useUpdateMaterial() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({ id, ...payload }: Partial<Omit<Material, 'id'>> & { id: number }) => {
            const { data } = await api.patch<Material>(`${API_ROUTES.adminMaterials}${id}/`, payload)
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: MATERIAL_KEYS.all })
        },
    })
}

export function useDeleteMaterial() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (id: number) => {
            await api.delete(`${API_ROUTES.adminMaterials}${id}/`)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: MATERIAL_KEYS.all })
        },
    })
}

// ─── Sizes ────────────────────────────────────────────────────────────────────

const SIZE_KEYS = {
    all: ['admin-sizes'] as const,
    list: () => [...SIZE_KEYS.all, 'list'] as const,
}

export function useAdminSizes() {
    const { isLogged } = useAuthStore()
    return useQuery({
        queryKey: SIZE_KEYS.list(),
        queryFn: async () => {
            const { data } = await api.get<{ results: PrintSize[] } | PrintSize[]>(API_ROUTES.adminSizes)
            return Array.isArray(data) ? data : data.results
        },
        enabled: isLogged,
    })
}

export function useCreateSize() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload: Omit<PrintSize, 'id'>) => {
            const { data } = await api.post<PrintSize>(API_ROUTES.adminSizes, payload)
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: SIZE_KEYS.all })
        },
    })
}

export function useUpdateSize() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({ id, ...payload }: Partial<Omit<PrintSize, 'id'>> & { id: number }) => {
            const { data } = await api.patch<PrintSize>(`${API_ROUTES.adminSizes}${id}/`, payload)
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: SIZE_KEYS.all })
        },
    })
}

export function useDeleteSize() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (id: number) => {
            await api.delete(`${API_ROUTES.adminSizes}${id}/`)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: SIZE_KEYS.all })
        },
    })
}

// ─── Prices ───────────────────────────────────────────────────────────────────

const PRICE_KEYS = {
    all: ['admin-prices'] as const,
    list: () => [...PRICE_KEYS.all, 'list'] as const,
}

export function useAdminPrices() {
    const { isLogged } = useAuthStore()
    return useQuery({
        queryKey: PRICE_KEYS.list(),
        queryFn: async () => {
            const { data } = await api.get<{ results: PriceConfig[] } | PriceConfig[]>(API_ROUTES.adminPrices)
            return Array.isArray(data) ? data : data.results
        },
        enabled: isLogged,
    })
}

export function useCreatePrice() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload: Omit<PriceConfig, 'id' | 'material_name' | 'size_name' | 'tiers' | 'allow_half_millar'>) => {
            const { data } = await api.post<PriceConfig>(API_ROUTES.adminPrices, payload)
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: PRICE_KEYS.all })
        },
    })
}

export function useUpdatePrice() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({ id, ...payload }: Partial<Omit<PriceConfig, 'id' | 'material_name' | 'size_name' | 'tiers' | 'allow_half_millar'>> & { id: number }) => {
            const { data } = await api.patch<PriceConfig>(`${API_ROUTES.adminPrices}${id}/`, payload)
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: PRICE_KEYS.all })
        },
    })
}

export function useDeletePrice() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (id: number) => {
            await api.delete(`${API_ROUTES.adminPrices}${id}/`)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: PRICE_KEYS.all })
        },
    })
}

// ─── Volume Tiers ────────────────────────────────────────────────────────────

const TIER_KEYS = {
    all: ['admin-volume-tiers'] as const,
    list: (priceConfigId?: number) => [...TIER_KEYS.all, 'list', priceConfigId] as const,
}

export interface VolumeTierPayload {
    price_config: number
    min_millares: number
    price_per_millar: string
}

export function useAdminVolumeTiers(priceConfigId?: number) {
    const { isLogged } = useAuthStore()
    return useQuery({
        queryKey: TIER_KEYS.list(priceConfigId),
        queryFn: async () => {
            const params = priceConfigId ? `?price_config=${priceConfigId}` : ''
            const { data } = await api.get<{ results: VolumeTier[] } | VolumeTier[]>(
                `${API_ROUTES.adminVolumeTiers}${params}`
            )
            return Array.isArray(data) ? data : data.results
        },
        enabled: isLogged && !!priceConfigId,
    })
}

export function useCreateVolumeTier() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload: VolumeTierPayload) => {
            const { data } = await api.post<VolumeTier>(`${API_ROUTES.adminVolumeTiers}`, payload)
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: TIER_KEYS.all })
            queryClient.invalidateQueries({ queryKey: PRICE_KEYS.all })
        },
    })
}

export function useUpdateVolumeTier() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({ id, ...payload }: VolumeTierPayload & { id: number }) => {
            const { data } = await api.patch<VolumeTier>(`${API_ROUTES.adminVolumeTiers}${id}/`, payload)
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: TIER_KEYS.all })
            queryClient.invalidateQueries({ queryKey: PRICE_KEYS.all })
        },
    })
}

export function useDeleteVolumeTier() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (id: number) => {
            await api.delete(`${API_ROUTES.adminVolumeTiers}${id}/`)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: TIER_KEYS.all })
            queryClient.invalidateQueries({ queryKey: PRICE_KEYS.all })
        },
    })
}

// ─── Shipping ─────────────────────────────────────────────────────────────────

const SHIPPING_KEYS = {
    all: ['admin-shipping'] as const,
    list: () => [...SHIPPING_KEYS.all, 'list'] as const,
}

export function useAdminShipping() {
    const { isLogged } = useAuthStore()
    return useQuery({
        queryKey: SHIPPING_KEYS.list(),
        queryFn: async () => {
            const { data } = await api.get<{ results: ShippingConfig[] } | ShippingConfig[]>(API_ROUTES.adminShipping)
            return Array.isArray(data) ? data : data.results
        },
        enabled: isLogged,
    })
}

export function useCreateShipping() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload: Omit<ShippingConfig, 'id'>) => {
            const { data } = await api.post<ShippingConfig>(API_ROUTES.adminShipping, payload)
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: SHIPPING_KEYS.all })
        },
    })
}

export function useUpdateShipping() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({ id, ...payload }: Partial<Omit<ShippingConfig, 'id'>> & { id: number }) => {
            const { data } = await api.patch<ShippingConfig>(`${API_ROUTES.adminShipping}${id}/`, payload)
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: SHIPPING_KEYS.all })
        },
    })
}

export function useDeleteShipping() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (id: number) => {
            await api.delete(`${API_ROUTES.adminShipping}${id}/`)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: SHIPPING_KEYS.all })
        },
    })
}
